/* eslint-disable react/destructuring-assignment */
import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import ReactTooltip from 'react-tooltip';
import _debounce from 'lodash/debounce';
import PropTypes from 'prop-types';

import updateAllPadding from '../updateAllPadding';
import Input from '../../../../componentLibrary/Input';
import ElementPadding from '../../../../componentLibrary/ElementPadding';
import paddingChange from '../../paddingChange';
import HorizontalRule from '../../../../componentLibrary/HorizontalRule';
import ButtonGroup from '../../../../componentLibrary/ButtonGroup/ButtonGroup';
import SurveyOptions from './SurveyOptions';
import Toggle from '../../../../componentLibrary/Toggle';
import * as columnItemActions from '../../../../actions/columnItem';
import * as canvasErrorActions from '../../../../actions/elementErrors';
import rules from '../../../../constants/businessRules/businessRules';
import * as Styled from '../styles';
import InputValidator from '../../../../componentLibrary/InputValidator';

const menuIndex = {
    [rules.surveyTypes.SOCIAL_REACTION_EMOJI]: { btnGroupIndex: 0, group: 'reaction' },
    [rules.surveyTypes.SOCIAL_REACTION_LIKE]: { btnGroupIndex: 0, group: 'reaction' },
    [rules.surveyTypes.PULSE_RANGE_STARS]: { btnGroupIndex: 1, group: 'range' },
    [rules.surveyTypes.PULSE_RANGE_NUMBERS]: { btnGroupIndex: 1, group: 'range' },
    [rules.surveyTypes.PULSE_RANGE_FACES]: { btnGroupIndex: 1, group: 'range' },
    [rules.surveyTypes.ENPS]: { btnGroupIndex: 1, group: 'range' },
    [rules.surveyTypes.PULSE_BINARY_CHECKBOX_CONFIRM]: { btnGroupIndex: 2, group: 'binary' },
    [rules.surveyTypes.PULSE_BINARY_THUMBS_UP_AND_DOWN]: { btnGroupIndex: 2, group: 'binary' },
    [rules.surveyTypes.PULSE_BINARY_YES_AND_NO]: { btnGroupIndex: 2, group: 'binary' },
};

const ValidatedSize = InputValidator(Input, { min: 0, max: 50 });
const ValidatedInput = InputValidator(Input, { required: true });

class SurveySetting extends PureComponent {
    state = {
        groupSelected: '',
        titleInput: this.props.selectedElementData.content.surveyTitle,
        commentsOption: this.props.selectedElementData.content.allowComments,
    };

    debouncedUpdateContent = _debounce(this.updateContent, 250);

    componentDidUpdate(prevProps) {
        const {
            selectedElementData: { elmIdx },
        } = prevProps;
        const {
            selectedElementData: {
                elmIdx: newElmIdx,
                content: { surveyTitle, allowComments },
            },
        } = this.props;

        // when the user clicks on another survey element, we need to reset local state so we do not "carry" values from the first element
        if (elmIdx !== newElmIdx) {
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({
                groupSelected: '',
                titleInput: surveyTitle,
                commentsOption: allowComments,
            });
        }
    }

    componentWillUnmount() {
        // when the user clicks on a canvasBlock, the selected element is removed from the state due to a dispatch on removeAllSelectedElements
        // turns out that onBlur never ends up running in this use case, not validating the size
        // to make up for this use case we call onBlur here in order to run the validation for the input size > 15
        this._sizeInput.props.onBlur();
    }

    updateContent(key, value) {
        const {
            selectedElementData: { blockIndex, columnIdx, elmIdx, content },
            dispatch,
        } = this.props;
        let newContentValues;
        if (key === 'updateAllPadding') {
            newContentValues = updateAllPadding(content, value);
        } else {
            newContentValues = { ...content, [key]: value };
        }
        const newContent = { ...content, ...newContentValues };
        dispatch(columnItemActions.updateColumnItemContent({ blockIndex, columnIdx, elmIdx, newContent }));
        if (key === 'surveyTitle') {
            dispatch(canvasErrorActions.deleteErroneousElement({ blockIndex, elmIdx }));
        }
    }

    render() {
        const { groupSelected, titleInput, commentsOption } = this.state;
        const {
            selectedElementData: { content },
        } = this.props;
        const { type, surveyTitle, iconSize, paddingTop, paddingRight, paddingBottom, paddingLeft } = content;

        return (
            <div>
                <Styled.Row>
                    <HorizontalRule />
                    <ElementPadding
                        paddingTop={paddingTop}
                        paddingBottom={paddingBottom}
                        paddingLeft={paddingLeft}
                        paddingRight={paddingRight}
                        elementType="survey"
                        onChange={(event) => {
                            const payload = paddingChange({
                                event,
                                paddingBottom,
                                paddingLeft,
                                paddingRight,
                                paddingTop,
                            });
                            if (!payload) {
                                return;
                            }
                            const { key, value = 0 } = payload;
                            this.updateContent(key, value);
                        }}
                    />
                    <HorizontalRule />
                    <ValidatedInput
                        marginBottom="0"
                        type="text"
                        width={180}
                        placeholder={surveyTitle === 'Survey Title' ? '' : null}
                        label="SURVEY TITLE"
                        value={titleInput}
                        onChange={(event) => {
                            const { value } = event.target;
                            this.setState({ titleInput: value }, () => {
                                const { titleInput: updatedTitleInput } = this.state;
                                this.debouncedUpdateContent('surveyTitle', updatedTitleInput);
                            });
                        }}
                        onBlur={() => {
                            if (!surveyTitle) {
                                this.updateContent('surveyTitle', 'Survey Title');
                            }
                        }}
                    />
                    <Styled.Info>
                        Identifies survey in email stats
                        <Styled.Icon data-tip data-for="survey-title-help" className="material-icons">
                            help_outline
                        </Styled.Icon>
                        <ReactTooltip
                            className="react-tooltip-text-wrapper"
                            id="survey-title-help"
                            place="bottom"
                            effect="solid"
                        >
                            <span data-multiline style={{ fontWeight: 'bold', lineHeight: '12px' }}>
                                Update your survey title here, so it appears in your email report.
                            </span>
                        </ReactTooltip>
                    </Styled.Info>
                    <HorizontalRule styling={{ marginTop: 10 }} />
                    <div style={{ color: '#424242', fontWeight: 600, marginBottom: 5, letterSpacing: 1, fontSize: 12 }}>
                        SURVEY STYLE
                    </div>
                    <ButtonGroup
                        selected={menuIndex[type].btnGroupIndex}
                        options={['Reaction', 'Range', 'Binary']}
                        onClick={(choice) => {
                            if (choice === groupSelected) {
                                return;
                            }
                            this.setState({ groupSelected: choice });
                        }}
                    />
                    <SurveyOptions
                        group={groupSelected || menuIndex[type].group}
                        selectedSurvey={type}
                        onClick={(selected) => {
                            if (selected === type) {
                                return;
                            }
                            this.updateContent('type', selected);
                        }}
                    />
                    <HorizontalRule />
                    <Styled.AllowCommentsDiv id="allow-comments-for-surveys">
                        <Styled.AllowCommentsText>
                            FEEDBACK PAGE
                            <Styled.Icon data-tip data-for="survey-comment-help" className="material-icons">
                                help_outline
                            </Styled.Icon>
                            <ReactTooltip
                                className="react-tooltip-text-wrapper"
                                id="survey-comment-help"
                                place="bottom"
                                effect="solid"
                            >
                                <span data-multiline style={{ fontWeight: 'bold', lineHeight: '12px' }}>
                                    Allow recipients to write comments after responding to this survey.
                                </span>
                            </ReactTooltip>
                        </Styled.AllowCommentsText>
                        <Toggle
                            initialValue={commentsOption}
                            onToggle={() => {
                                this.setState({ commentsOption: !commentsOption }, () => {
                                    this.updateContent('allowComments', !commentsOption);
                                });
                            }}
                        />
                    </Styled.AllowCommentsDiv>
                    <HorizontalRule />
                    <Styled.Label>icon size</Styled.Label>
                    <ValidatedSize
                        ref={(input) => {
                            this._sizeInput = input;
                        }}
                        type="number-small"
                        width={180}
                        initialValue={iconSize || ''}
                        label="Icon Size"
                        icon="fullscreen"
                        onChange={(event) => {
                            const value = +event.target.value;
                            if (value === iconSize) {
                                return;
                            }

                            this.updateContent('iconSize', value);
                        }}
                        onBlur={() => {
                            if (iconSize < 15) {
                                this.updateContent('iconSize', 15);
                            }
                        }}
                    />
                    <HorizontalRule />
                    <Styled.LinkTipDiv>
                        <Styled.LinkIcon icon="external-link" />
                        <Styled.Link
                            href="https://support.staffbase.com/hc/en-us/articles/4412584515858"
                            rel="noopener noreferrer"
                            target="_blank"
                        >
                            How to increase engagement
                        </Styled.Link>
                    </Styled.LinkTipDiv>
                </Styled.Row>
            </div>
        );
    }
}

SurveySetting.propTypes = {
    dispatch: PropTypes.func.isRequired,
    selectedElementData: PropTypes.object.isRequired,
};

export default connect()(SurveySetting);
